import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";

const SeoCtx = createContext({});
const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Site assets are served by the backend; make them absolute so og:image / favicon work everywhere.
export const absAsset = (path) => (path ? `${BACKEND}${path}` : "");
export const isPublicPath = (p) => !p.startsWith("/app") && !p.startsWith("/admin");

export function SeoProvider({ children }) {
  const [seo, setSeo] = useState({});
  useEffect(() => {
    const load = () => api.get("/public/seo").then((r) => setSeo(r.data)).catch(() => {});
    load();
    window.addEventListener("seo-updated", load);
    return () => window.removeEventListener("seo-updated", load);
  }, []);
  return <SeoCtx.Provider value={seo}>{children}</SeoCtx.Provider>;
}

export const useSeo = () => useContext(SeoCtx);

function upsert(selector, create, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
}

// Admin-provided head HTML: scripts must be re-created to execute.
function injectHtml(html) {
  const tpl = document.createElement("template");
  tpl.innerHTML = html;
  [...tpl.content.childNodes].forEach((n) => {
    if (n.nodeType !== 1) return;
    let el = n;
    if (n.nodeName === "SCRIPT") {
      el = document.createElement("script");
      [...n.attributes].forEach((a) => el.setAttribute(a.name, a.value));
      el.text = n.text;
    }
    el.setAttribute("data-site-head", "1");
    document.head.appendChild(el);
  });
}

// Favicon, Search Console tag, GA4 and custom head scripts. GA4 and custom scripts load only on public pages.
export function SiteHead() {
  const seo = useSeo();
  const { pathname } = useLocation();
  const loaded = useRef({ ga: false, scripts: false });

  useEffect(() => {
    if (!seo.favicon) return;
    const href = absAsset(seo.favicon);
    upsert('link[rel="icon"]', () => Object.assign(document.createElement("link"), { rel: "icon" }), { href, type: "image/png" });
    upsert('link[rel="apple-touch-icon"]', () => Object.assign(document.createElement("link"), { rel: "apple-touch-icon" }), { href });
  }, [seo.favicon]);

  useEffect(() => {
    const sel = 'meta[name="google-site-verification"]';
    if (seo.gsc_code) upsert(sel, () => Object.assign(document.createElement("meta"), { name: "google-site-verification" }), { content: seo.gsc_code });
    else document.head.querySelector(sel)?.remove();
  }, [seo.gsc_code]);

  useEffect(() => {
    if (!isPublicPath(pathname)) return;
    if (seo.ga_id && !loaded.current.ga) {
      loaded.current.ga = true;
      const s = document.createElement("script");
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(seo.ga_id)}`;
      document.head.appendChild(s);
      window.dataLayer = window.dataLayer || [];
      window.gtag = function gtag() { window.dataLayer.push(arguments); };
      window.gtag("js", new Date());
      window.gtag("config", seo.ga_id, { send_page_view: false });
    }
    if (seo.head_scripts && !loaded.current.scripts) {
      loaded.current.scripts = true;
      injectHtml(seo.head_scripts);
    }
  }, [seo.ga_id, seo.head_scripts, pathname]);

  // SPA navigation: report each public page view to GA4.
  useEffect(() => {
    if (!seo.ga_id || !window.gtag || !isPublicPath(pathname)) return undefined;
    const id = setTimeout(() => window.gtag("event", "page_view", { page_location: window.location.href, page_path: pathname, page_title: document.title }), 0);
    return () => clearTimeout(id);
  }, [seo.ga_id, pathname]);

  return null;
}
